import { Editor, useEditorState } from '@tiptap/react';
import { FunctionComponent, KeyboardEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isUrl } from '../../../../utils';
import { Button, ButtonType } from '../../../Button';
import { IconLabel } from '../../../IconLabel';
import { InputField } from '../../../InputField';

import classNames from 'classnames';
import classes from '../InlineTools.module.scss';

interface LinkEditorProps {
  editor: Editor;
  onClose: () => void;
}

export const LinkEditor: FunctionComponent<LinkEditorProps> = ({ editor, onClose }) => {
  const { t } = useTranslation();

  const { isActive } = useEditorState({
    editor,
    selector: ({ editor }) => ({ isActive: editor.isActive('link') }),
  });

  const [url, setUrl] = useState<string>(editor.getAttributes('link').href ?? '');

  const isValid = !url.length || isUrl(url) || url.startsWith('mailto:') || url.startsWith('tel:');

  const canOpen = Boolean(url) && isValid;

  const applyHandler = useCallback(() => {
    if (!isValid) {
      return;
    }

    const markRange = editor.chain().focus().extendMarkRange('link');

    if (url === '') {
      markRange.unsetLink().run();
    } else {
      markRange.setLink({ href: url }).run();
    }

    onClose();
  }, [editor, isValid, onClose, url]);

  const removeHandler = useCallback(() => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    onClose();
  }, [editor, onClose]);

  const keyDownHandler = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== 'Enter') {
        return;
      }

      event.preventDefault();
      applyHandler();
    },
    [applyHandler]
  );

  return (
    <>
      <IconLabel
        iconId={'link'}
        iconSize={16}
        nonClickable
        className={classes['inline-tools__link-icon']}
      />

      <InputField
        value={url}
        onChange={({ target }) => setUrl(target.value)}
        placeholder={'https://'}
        className={classes['inline-tools__link-field']}
        inputClassName={classes['inline-tools__link-input']}
        onKeyDown={keyDownHandler}
        autoFocus
      />

      <Button
        type={ButtonType.primary}
        label={t('common.apply')}
        onClick={applyHandler}
        disabled={!isValid}
      />

      <IconLabel
        iconId={'arrow-rounded-right-up-box'}
        iconSize={16}
        ariaLabel={t('common.open-in-new-tab')}
        onClick={() => window.open(url, '_blank', 'noopener, noreferrer')}
        disabled={!canOpen}
        className={classNames(classes['inline-tools__item'], {
          [classes['inline-tools__item--disabled']]: !canOpen,
        })}
      />

      {isActive && (
        <IconLabel
          iconId={'delete'}
          iconSize={16}
          ariaLabel={t('common.remove')}
          onClick={removeHandler}
          className={classes['inline-tools__item']}
        />
      )}
    </>
  );
};
